/**
 * `travel` — ONE collection for every journey leg, replacing v1's duplicated
 * flights+transport (ARCHITECTURE.md §3.2, the flagship of the registry).
 * One row per person per leg (group rides share a group_id). Every leg has
 * full departure AND arrival date+time — red-eyes and the Oceania dateline
 * ("sometimes we timetravel ahaha") are first-class, never inferred.
 * Layovers are child legs: same group_id, leg_index 1..n.
 */
import { z } from "zod";
import { defineCollection, field } from "../define.ts";

const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD (venue-local)");
const WALL_TIME = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "expected HH:MM venue-local wall time");
const KINDS = ["flight", "train", "car", "van", "boat", "walk", "other"] as const;

export const travel = defineCollection({
  key: "travel", // the ONLY place this string is born (CONSTRAINTS #5)
  labelKey: "collections.travel",
  icon: "plane",
  sheetTab: "Travel",
  dedupFields: ["kind", "dep_day", "dep_time", "ref", "person_id"],
  sort: [{ field: "dep_day" }, { field: "dep_time" }],
  split: "day",
  chatHint:
    "Journey legs: flights, trains, cars/vans, boats, walks. ONE ROW PER PERSON per leg — " +
    "never 'Neto + Thibault' in one row. Layovers are extra rows sharing group_id with " +
    "leg_index 1,2,…. Departure AND arrival carry full date+time (overnight and dateline " +
    "legs are normal). v1's flights and transport collections both map here.",
  fields: {
    kind: field({
      type: z.enum(KINDS),
      tier: "hot",
      pii: "crew",
      labelKey: "travel.kind",
      renamedFrom: ["mode"], // v1 transport wire name (migration lineage, CONSTRAINTS #6)
      sheet: { column: "Kind" },
      description: "flight | train | car | van | boat | walk | other. Gates which detail fields apply.",
    }),
    role: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.role",
      renamedFrom: ["type"], // v1 transport wire name
      enum: { values: ["transit", "pickup"], open: true },
      sheet: { column: "Role" },
      description: "Ground-leg role: transit (between cities) or pickup (someone collects you).",
    }),
    person_id: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "crew",
      input: "person",
      labelKey: "travel.person",
      sheet: { column: "Person" },
      description: "The traveller this row belongs to (one row per person). Empty = whole party (rare).",
    }),
    group_id: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.group",
      sheet: { column: "Group" },
      description: "Shared by rows of one journey: the same ride's passengers, and a trip's layover legs.",
    }),
    leg_index: field({
      type: z.number().int().min(0).optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.leg_index",
      sheet: { column: "Leg" },
      description: "0 = first leg; 1,2,… = layover legs of the same group_id journey.",
    }),
    dep_place: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "travel.dep_place",
      wireName: "dep_place",
      renamedFrom: ["from", "dep_city"],
      sheet: { column: "From" },
      description: "Departure place — city, station, hotel, venue. v1 'from'/'dep_city' map here.",
    }),
    dep_iata: field({
      type: z.string().max(4).optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.dep_iata",
      visibleWhen: { field: "kind", in: ["flight"] },
      sheet: { column: "From IATA" },
      description: "Departure airport code (flights only), e.g. TLS, CDG.",
    }),
    dep_day: field({
      type: DAY.optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "travel.dep_day",
      renamedFrom: ["date"], // v1 wire name (migration lineage)
      sheet: { column: "Dep day" },
      description: "Departure date (YYYY-MM-DD, local to the departure place).",
    }),
    dep_time: field({
      type: WALL_TIME.optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "travel.dep_time",
      renamedFrom: ["time"],
      sheet: { column: "Dep" },
      description: "Departure time (HH:MM local wall time). v1 transport 'time' maps here.",
    }),
    dep_tz: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.dep_tz",
      input: "tz",
      sheet: { column: "Dep tz" },
      description:
        "IANA time zone of the DEPARTURE place (e.g. Europe/Paris). ADR-006: defaults to the " +
        "gig's timezone when unset — set it on legs that leave from a different zone " +
        "(Paris→Mexico City departs on Paris time), or the travel-window math runs hours off.",
    }),
    arr_place: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "travel.arr_place",
      renamedFrom: ["to", "arr_city"],
      sheet: { column: "To" },
      description: "Arrival place. v1 'to'/'arr_city' map here.",
    }),
    arr_iata: field({
      type: z.string().max(4).optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.arr_iata",
      visibleWhen: { field: "kind", in: ["flight"] },
      sheet: { column: "To IATA" },
      description: "Arrival airport code (flights only).",
    }),
    arr_day: field({
      type: DAY.optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "travel.arr_day",
      renamedFrom: ["end_date"], // v1 schedule-travel wire name
      sheet: { column: "Arr day" },
      description: "Arrival date — MAY differ from dep_day (red-eyes, dateline crossings). Never assume same-day.",
    }),
    arr_time: field({
      type: WALL_TIME.optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "travel.arr_time",
      renamedFrom: ["end_time"], // v1 schedule-travel wire name
      sheet: { column: "Arr" },
      description:
        "Arrival time (HH:MM local wall time). Often unknown at booking — leave empty; " +
        "the suggestions system nudges the TM to complete it later. NEVER invent one.",
    }),
    arr_tz: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.arr_tz",
      input: "tz",
      sheet: { column: "Arr tz" },
      description:
        "IANA time zone of the ARRIVAL place. ADR-006: defaults to the gig's timezone when " +
        "unset. Arrival wall time is what the ticket prints, in the arrival city's zone.",
    }),
    carrier: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.carrier",
      renamedFrom: ["airline"],
      sheet: { column: "Carrier" },
      description: "Airline / rail operator / company. v1 'airline' maps here.",
      variantOverrides: {
        flight: { labelKey: "travel.carrier.flight" },
        train: { labelKey: "travel.carrier.train" },
      },
    }),
    ref: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "travel.ref",
      renamedFrom: ["flight_no", "train_no"],
      sheet: { column: "Ref" },
      description: "Flight number / train number / booking reference. v1 flight_no + train_no map here.",
    }),
    seat: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew_self",
      labelKey: "travel.seat",
      sheet: { column: "Seat" },
      description: "Seat / carriage-seat. Per-person (rows are per-person by design).",
    }),
    carriage: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew_self",
      labelKey: "travel.carriage",
      visibleWhen: { field: "kind", in: ["train"] },
      sheet: { column: "Carriage" },
      description: "Train carriage (trains only).",
    }),
    platform: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.platform",
      visibleWhen: { field: "kind", in: ["train"] },
      sheet: { column: "Platform" },
      description: "Departure platform (trains only).",
    }),
    gate: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.gate",
      visibleWhen: { field: "kind", in: ["flight"] },
      sheet: { column: "Gate" },
      description: "Boarding gate (flights only) — feeds the Travel Card (ADR-011).",
    }),
    terminal: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.terminal",
      visibleWhen: { field: "kind", in: ["flight"] },
      sheet: { column: "Terminal" },
      description: "Airport terminal (flights only).",
    }),
    vehicle: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.vehicle",
      visibleWhen: { field: "kind", in: ["car", "van"] },
      sheet: { column: "Vehicle" },
      description: "Vehicle description or plate (car/van legs).",
    }),
    driver_name: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.driver_name",
      visibleWhen: { field: "kind", in: ["car", "van"] },
      sheet: { column: "Driver" },
      description: "Driver's name (car/van legs).",
    }),
    driver_phone: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.driver_phone",
      input: "tel",
      visibleWhen: { field: "kind", in: ["car", "van"] },
      sheet: { column: "Driver phone" },
      description: "Driver's phone (car/van legs).",
    }),
    vessel: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.vessel",
      visibleWhen: { field: "kind", in: ["boat"] },
      sheet: { column: "Vessel" },
      description: "Vessel name (boat legs).",
    }),
    dock: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.dock",
      visibleWhen: { field: "kind", in: ["boat"] },
      sheet: { column: "Dock" },
      description: "Dock / pier (boat legs).",
    }),
    status: field({
      type: z.enum(["on_time", "delayed", "cancelled", "tentative"]),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.status",
      sheet: { column: "Status" },
      description: "on_time | delayed | cancelled | tentative. Tentative = booked but unconfirmed.",
    }),
    notes: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "travel.notes",
      sheet: { column: "Notes" },
      description: "Free-form notes (pickup instructions, luggage, buyout context).",
    }),
  },
});
